import React from 'react';
import styled from 'styled-components';
import { sizes } from '../../theme';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

type TurnstileWidgetProps = {
    action: 'signin' | 'signup';
    onTokenChange: (token: string) => void;
    onError?: (error: string) => void;
    resetSignal: number;
};

export function TurnstileWidget({ action, onTokenChange, onError, resetSignal }: TurnstileWidgetProps): JSX.Element {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const widgetIdRef = React.useRef<string>();

    React.useEffect(() => {
        let cancelled = false;

        async function renderTurnstile() {
            try {
                await loadTurnstileScript();
            } catch (error) {
                console.warn('Cloudflare Turnstile script was blocked by the browser (likely an ad-blocker or privacy shield).');
                onTokenChange('');
                onError?.('Security check was blocked. If you are using a strict ad-blocker or privacy shield, please disable it to sign in.');
                return;
            }

            if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current) return;

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: TURNSTILE_SITE_KEY!,
                action,
                theme: 'light',
                retry: 'auto',
                'retry-interval': 8000,
                callback: (token: string) => {
                    onError?.(''); // Clear any previous errors on success
                    onTokenChange(token);
                },
                'expired-callback': () => {
                    onTokenChange('');
                    if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
                },
                'timeout-callback': () => {
                    onTokenChange('');
                    if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
                },
                'error-callback': () => {
                    onTokenChange('');
                    onError?.('Security check failed. If you are using a VPN or strict privacy shields, you may need to disable them.');
                },
            } as any);
        }

        renderTurnstile();

        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = undefined;
            }
        };
    }, [action, onTokenChange, onError]);

    React.useEffect(() => {
        if (!widgetIdRef.current || !window.turnstile) return;
        window.turnstile.reset(widgetIdRef.current);
        onTokenChange('');
        onError?.('');
    }, [onTokenChange, onError, resetSignal]);

    return <TurnstileContainer ref={containerRef} />;
}

function loadTurnstileScript(): Promise<void> {
    if (window.turnstile) return Promise.resolve();

    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
        if (existingScript.dataset.loaded === 'true') return Promise.resolve();

        return new Promise((resolve, reject) => {
            existingScript.addEventListener('load', () => resolve(), { once: true });
            existingScript.addEventListener(
                'error',
                (error) => {
                    existingScript.remove();
                    reject(error);
                },
                { once: true },
            );
        });
    }

    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = TURNSTILE_SCRIPT_SRC;
        script.async = true;
        script.defer = true;
        script.addEventListener(
            'load',
            () => {
                script.dataset.loaded = 'true';
                resolve();
            },
            { once: true },
        );
        script.addEventListener(
            'error',
            (error) => {
                script.remove();
                reject(error);
            },
            { once: true },
        );
        document.head.appendChild(script);
    });
}

const TurnstileContainer = styled.div`
    min-height: 65px;
    margin: ${sizes.spacingSm} 0;
`;
